/**
 * A seeded random number generator.
 *
 * Every rolled thing in this game — which events are on the board, what they
 * are short of, when a favour gets called in, how badly an unattended room goes
 * — has to come back the same after a reload. A save that restores the day but
 * re-rolls the day's shortfalls is a save that quietly changes the game
 * underneath the player, and they will notice long before they can explain it.
 *
 * mulberry32: thirty-two bits of state, no dependencies, good enough for dice.
 * Each consumer takes its own stream seeded from the run seed plus a label, so
 * adding a new roll somewhere cannot shift every other roll in the game.
 */

export function hashSeed(...parts) {
  let hash = 0x811c9dc5;
  for (const part of parts) {
    const text = String(part);
    for (let i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
  }
  return hash >>> 0;
}

export function createRng(seed) {
  let state = (typeof seed === 'number' ? seed : hashSeed(seed)) >>> 0;

  function next() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  return {
    next,
    /** Integer in [min, max], inclusive. */
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    /** One of a list. */
    pick: (list) => list[Math.floor(next() * list.length)],
    /**
     * One of a list, by `weight`. Used everywhere a designer wants "this twice
     * as often as that" without writing a lookup table by hand.
     */
    weighted(list, weightOf = (entry) => entry.weight ?? 1) {
      const total = list.reduce((sum, entry) => sum + weightOf(entry), 0);
      if (total <= 0) return list[0];
      let roll = next() * total;
      for (const entry of list) {
        roll -= weightOf(entry);
        if (roll <= 0) return entry;
      }
      return list[list.length - 1];
    },
    /** Shuffles a copy. */
    shuffle(list) {
      const out = [...list];
      for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    },
    /**
     * Roughly normal, mean 0 and standard deviation 1. Two uniforms averaged
     * four times — not a real Box-Muller, and much better behaved at the tails,
     * which matters when the output is a grade somebody has to live with.
     */
    gauss() {
      let total = 0;
      for (let i = 0; i < 4; i += 1) total += next();
      return (total - 2) * 1.1;
    },
  };
}
